import React from "react";
import { UserGroupIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

function ParentDuplicateSelectionModal({
  duplicates,
  onSelectParent,
  onCreateNew,
  onCancel,
  parentNumber, // 1 or 2
  parentName,
}) {
  const hasDuplicates = duplicates && duplicates.length > 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white rounded-sm shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            {hasDuplicates ? (
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            ) : (
              <UserGroupIcon className="h-6 w-6 text-indigo-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {hasDuplicates
                  ? `Potential Duplicates Found for Parent ${parentNumber}`
                  : `No Duplicates Found for Parent ${parentNumber}`}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {hasDuplicates
                  ? `Found ${duplicates.length} potential duplicate${
                      duplicates.length > 1 ? "s" : ""
                    } for ${parentName}. Select an existing parent to link or create a new record.`
                  : `No existing parent records found matching ${parentName}. You can create a new parent record.`}
              </p>
            </div>
          </div>

          {hasDuplicates && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Select existing parent or create new:
              </p>

              {duplicates.map((duplicate) => (
                <button
                  key={duplicate._id}
                  onClick={() => onSelectParent(duplicate._id)}
                  className="w-full text-left p-4 border border-gray-200 rounded-sm hover:border-indigo-500 hover:bg-indigo-50/50 transition group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {duplicate.firstName} {duplicate.lastName}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          {duplicate.matchReason}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {duplicate.email && (
                          <p>
                            <span className="font-medium">Email:</span> {duplicate.email}
                          </p>
                        )}
                        {duplicate.phoneNumbers && duplicate.phoneNumbers.length > 0 && (
                          <p>
                            <span className="font-medium">Phone:</span>{" "}
                            {duplicate.phoneNumbers.map((p) => p.number).join(", ")}
                          </p>
                        )}
                        {duplicate.children && duplicate.children.length > 0 && (
                          <p>
                            <span className="font-medium">Children:</span>{" "}
                            {duplicate.children.map((c) => `${c.firstName} ${c.lastName}`).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-block px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-sm group-hover:bg-indigo-600 group-hover:text-white transition">
                        Select
                      </span>
                    </div>
                  </div>
                </button>
              ))}

              <div className="pt-2 border-t border-gray-200 mt-4">
                <button
                  onClick={onCreateNew}
                  className="w-full p-4 border-2 border-dashed border-gray-300 rounded-sm hover:border-indigo-500 hover:bg-gray-50 transition text-left">
                  <p className="font-medium text-gray-900">Create New Parent Record</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Create a new parent record for {parentName} instead of linking to an existing one
                  </p>
                </button>
              </div>
            </div>
          )}

          {!hasDuplicates && (
            <div className="space-y-3">
              <button
                onClick={onCreateNew}
                className="w-full p-4 border border-gray-300 rounded-sm hover:border-indigo-500 hover:bg-indigo-50/50 transition text-left">
                <p className="font-medium text-gray-900">Create New Parent Record</p>
                <p className="mt-1 text-sm text-gray-600">
                  Create a new parent record for {parentName}
                </p>
              </button>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentDuplicateSelectionModal;
